import * as Sentry from '@sentry/react';

interface TrackErrorOptions {
  extra?: Record<string, unknown>;
  force?: boolean; // Override to track even non-critical errors
}

const buildContextData = (context: string, extra: Record<string, unknown>) => {
  const combined: Record<string, unknown> = { contextName: context, ...extra };

  return Object.fromEntries(
    Object.entries(combined).filter(([, value]) => value !== undefined),
  ) as Record<string, unknown>;
};

export const trackError = (error: unknown, context: string, options: TrackErrorOptions = {}) => {
  const { extra = {}, force = false } = options;

  // Skip tracking non-critical errors unless forced
  if (!force && shouldSkipError(error)) {
    return;
  }

  const err =
    error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Unknown error');

  const contextData = buildContextData(context, extra);

  // Report to Sentry
  Sentry.captureException(err, {
    tags: {
      context,
    },
    contexts: {
      errorContext: contextData,
    },
  });
};

const shouldSkipError = (error: unknown): boolean => {
  // Check for ApiError objects (transformed errors from httpRequest)
  if (typeof error === 'object' && error !== null && 'type' in error && 'status' in error) {
    const apiError = error as { type: string; status?: number };
    // Skip 401 errors (token refresh/session expiry)
    if (apiError.status === 401 || apiError.type === 'unauthorized') {
      return true;
    }
    // Skip 403 errors (forbidden - often expected)
    if (apiError.status === 403 || apiError.type === 'forbidden') {
      return true;
    }
    // Skip 422 validation errors (user input issues, not system errors)
    if (apiError.status === 422 || apiError.type === 'unprocessableEntity') {
      return true;
    }
  }

  // Check for Axios errors
  if (isAxiosError(error)) {
    // Skip 401 errors (token refresh/session expiry)
    if (error.response?.status === 401) {
      return true;
    }
    // Skip 403 errors (forbidden - often expected)
    if (error.response?.status === 403) {
      return true;
    }
    // Skip 422 validation errors (user input issues, not system errors)
    if (error.response?.status === 422) {
      return true;
    }
  }

  return false;
};

const isAxiosError = (error: unknown): error is { response?: { status?: number } } => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: unknown }).response === 'object'
  );
};
